<template>
  <component :is="$vuetify.breakpoint.mdAndUp ? 'v-menu' : 'v-dialog'"
             content-class="recurrence-selector-content"
             :width="'unset'"
             offset-y
             :left="left"
             :min-width="'unset'"
             :close-on-content-click="false"
             :value="isMenuOpen"
             @input="handleInput">
    <template v-slot:activator="{on, attrs}">
      <v-tooltip
        :top="true"
      >
        <template v-slot:activator="tooltipObj">
          <v-layout
            v-on="{...on, ...tooltipObj.on}"
            v-bind="{...attrs, ...tooltipObj.attrs}"
            class="recurrence-string">
            <v-icon size="12">
              icon-Pending
            </v-icon>
            <div class="text">
              {{recurrenceDescription}}
            </div>
          </v-layout>

        </template>
        {{recurrenceDescription}}
      </v-tooltip>

    </template>
    <v-card class="recurrence-selector-menu-content">
      <v-form ref="form">
        <v-layout column>
          <v-layout
            v-if="!$vuetify.breakpoint.mdAndUp"
            class="mobile-header"
            align-center
            justify-space-between>
          <span class="mobile-title">
            {{$t('common.words.repeat')}}
          </span>
            <v-btn icon @click="handleInput(false)">
              <v-icon size="20">close</v-icon>
            </v-btn>
          </v-layout>
          <VcitaAlert
            v-if="recurringPassedLimit"
            :type="'attention'"
            :body-text="$t('appointment.action.new.new_dialog.recurrence.recurring_limit_number', {limit_number: recurring_limit_number})"
            :isStripe="true"
          />
          <v-layout class="recurrence-inputs" column>
            <VcitaSwitch
              class="recurrence-switch"
              v-model="recurrenceParams.is_recurring"
              :label="$t('common.words.repeat')"
            />
            <v-layout>
              <VcitaTextField
                class="frequency-input"
                :disabled="!recurrenceParams.is_recurring"
                :label="$t('event.recurrence.every')"
                v-model="recurrenceParams.frequency"
                min="1"
                step="1"
                type="number"/>
              <VcitaSelectDropDown
                :disabled="!recurrenceParams.is_recurring"
                v-model="recurrenceParams.rule_type"
                class="recurrence-type-select"
                :items="recurrenceTypes"
              />
            </v-layout>
            <v-radio-group
              v-if="recurrenceParams.rule_type === 'monthly'"
              class="monthly-type-radio"
              hide-details="'auto'"
              v-model="recurrenceParams.monthly_type"
              :disabled="!recurrenceParams.is_recurring"
            >
              <v-radio
                color="#54D28C"
                value="day_of_month"
                :label="$t('event.recurrence.monthly_type.day_of_month')">
              </v-radio>
              <v-radio
                color="#54D28C"
                value="day_of_week"
                :label="$t('event.recurrence.monthly_type.day_of_week')">
              </v-radio>
            </v-radio-group>
            <template v-if="recurrenceParams.rule_type === 'weekly'">
              <v-layout class="day-buttons-title">
                {{$t('event.recurrence.repeat_on')}}
              </v-layout>
              <v-btn-toggle
                class="weekly-days-select"
                v-model="recurrenceParams.days_of_week"
                multiple
              >
                <v-btn
                  :disabled="!recurrenceParams.is_recurring"
                  v-for="n in 7"
                  elevation="0"
                  :color="activeDaysMap[daysOfWeekNames[n-1]] ? brandBgColor : ''"
                  class="day-btn"
                  rounded
                  icon
                  height="24px"
                  max-width="24px"
                  min-width="24px"
                  width="24px"
                  :value="daysOfWeekNames[n-1]">
                  {{i18nDaysOfWeekNames[n-1]}}
                </v-btn>
              </v-btn-toggle>
            </template>

            <v-layout>
              <VcitaSelectDropDown
                :disabled="!recurrenceParams.is_recurring"
                v-model="recurrenceParams.endsType"
                class="end-type-select"
                :label="$t('event.recurrence.ends')"
                :items="endingTypes"
              />
              <DatePicker
                :disabled="!recurrenceParams.is_recurring"
                v-model="recurrenceParams.til"
                v-if="recurrenceParams.endsType === 'on'"
                :min="new Date().toISOString()"
              />
              <VcitaTextField
                class="times-input"
                v-if="recurrenceParams.endsType === 'after'"
                :disabled="!recurrenceParams.is_recurring"
                :label="$t('event.recurrence.times')"
                v-model="recurrenceParams.count"
                min="1"
                max="100"
                step="1"
                type="number"
              />
            </v-layout>
          </v-layout>

          <v-layout class="recurrence-footer" justify-end>
            <v-btn
              @click="cancelDialog"
              class="btn cancel-btn"
              :elevation="0"
              text
            >
              {{$t('common.dialog.cancel')}}
            </v-btn>
            <v-btn
              @click="handleInput(false)"
              class="btn done-btn"
              :elevation="0"
              :color="brandBgColor"
              :disabled="recurringPassedLimit"
            >
              {{$t('common.dialog.done')}}
            </v-btn>
          </v-layout>
        </v-layout>
      </v-form>

    </v-card>
  </component>

</template>

<script>
  import VcitaSwitch from "@/modules/common/components/VcitaSwitch/VcitaSwitch.vue"
  import VcitaSelectDropDown from "@/modules/common/components/VcitaSelectDropDown/VcitaSelectDropDown.vue"
  import { DatePicker, VcitaTextField, VcitaAlert} from "@/modules/common/components"
  import {getRecurrenceDescription} from "@/modules/bookings/services/bookingsService.js"
  import {createDateAsUTC} from "@/modules/common/services/timeService.js"
  const db = require('just-debounce');

  export default {
    name: 'RecurrenceSelector',
    components: {
      VcitaSwitch,
      VcitaSelectDropDown,
      VcitaTextField,
      DatePicker,
      VcitaAlert

    },
    watch: {
      value: {
        handler(newVal, oldVal) {
          if(JSON.stringify(newVal) === JSON.stringify(oldVal)) {
            return
          }
          if(!newVal.days_of_week) {
            delete newVal.days_of_week
          }
          if(typeof newVal.days_of_week === 'object') {
            newVal.days_of_week = Object.values(newVal.days_of_week)
          }
          if(!newVal.is_recurring && (oldVal && oldVal.is_recurring)) {
            Object.assign(this.recurrenceParams, this.currentParams)
          } else {
            Object.assign(this.recurrenceParams, newVal)
          }
          this.$emit('input', this.recurrenceParams)
        },
        immediate: true,
      },
      'recurrenceParams.rule_type': {
        handler(newVal) {
          if(newVal === 'weekly') {
            this.recurrenceParams.endsType = 'on'
          } else {
            this.recurrenceParams.endsType = 'after'
          }
        },
        immediate: true,
      },
      formattedRecurrenceParams: {
        handler(newVal) {
          this.debouncedGetDescription(newVal)
          this.$emit('formatted-input', newVal)
        },
        immediate: true,
      },
      isMenuOpen: {
        async handler(newVal) {
          if(newVal && this.isValid) {
            this.currentParams = JSON.parse(JSON.stringify(this.recurrenceParams))
          }
        },
      },
    },
    props: {
      value: {
        type: Object,
        required: true,
      },
      start_time: {
        type: Date,
        default: () => new Date(),
      },
      duration: {
        type: Number,
        required: true
      },
      left: {
        type: Boolean,
        default: false
      },
      excludeNever: {
        type:Boolean,
        default: false
      }
    },
    data() {
      const daysOfWeekNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday',  'saturday', 'sunday']
      let i18nDaysOfWeekNames = daysOfWeekNames.map(day => {
        return this.$t(`common.days_letters.${day}`)
      })
      let endByDefault = new Date(this.start_time)
      endByDefault.setDate(endByDefault.getDate() + 30)
      endByDefault.setHours(endByDefault.getHours(), endByDefault.getMinutes() + this.duration)
      const endingTypes = [
        {
          value: 'after',
          text: this.$t('event.recurrence.after'),
        },
        {
          value: 'on',
          text: this.$t('event.recurrence.on_date'),
        },
      ]

      if (this.$hasFeature('endless_booking') && !this.excludeNever) {
        endingTypes.unshift({
          value: 'never',
          text: this.$t('common.words.never'),
        })
      }
      return {
        recurring_limit_number: 70,
        currentParams: {},
        recurrenceParams: {
          is_recurring: false,
          frequency: 1,
          rule_type: 'weekly',
          endsType: 'after',
          count: 10,
          til: endByDefault.toISOString(),
          monthly_type: 'day_of_month',
          days_of_week: [this.getWeekDay(daysOfWeekNames)],
        },
        daysOfWeekNames,
        i18nDaysOfWeekNames,
        isMenuOpen: false,
        recurrenceDescription: '',
        recurrenceCount: null,
        endingTypes,
        debouncedGetDescription: db(this.updateDescription, 500),
      }
    },
    methods: {
      cancelDialog() {
        Object.assign(this.recurrenceParams, this.currentParams)
        this.isMenuOpen = false
      },
      async handleInput(val) {
        await this.$nextTick()
        this.isMenuOpen = val
        if(!this.isValid && !val) {
          await this.$nextTick()
          this.isMenuOpen = true
        }
      },
      async updateDescription(newVal) {
        if(newVal.is_recurring && this.isValid) {
          let res = await getRecurrenceDescription(newVal);
          if(newVal === this.formattedRecurrenceParams) {
            this.recurrenceDescription = res.data.description
            this.recurrenceCount = res.data.count
          }
        } else {
          this.recurrenceDescription = this.$t('appointment.action.new.new_dialog.recurrence.does_not_repeat')
          this.recurrenceCount = null
        }
      },
      getWeekDay(daysOfWeekNames) {
        let res = this.start_time.getDay()
        if (!res) { // for sunday
          res = daysOfWeekNames.length
        }
        return daysOfWeekNames[res - 1]
      }
    },
    computed: {
      recurrenceTypes() {
        const isPlural = this.recurrenceParams.frequency > 1
        let pluralString = ''
        if(isPlural) {
          pluralString = 's'
        }
        return [
          {
            value: 'daily',
            text: this.$t(`settings.packages.package_page.fields.day${pluralString}`),
          },
          {
            value: 'weekly',
            text: this.$t(`settings.packages.package_page.fields.week${pluralString}`),
          },
          {
            value: 'monthly',
            text: this.$t(`settings.packages.package_page.fields.month${pluralString}`),
          },
          {
            value: 'yearly',
            text: this.$t(`settings.packages.package_page.fields.year${pluralString}`),
          },
        ]
      },
      brandBgColor() {
        return this.$store && this.$store.getters['brandModule/secondaryColor'];
      },
      activeDaysMap() {
        return this.recurrenceParams.days_of_week.reduce((acc, day) => {
          acc[day] = true
          return acc
        }, {})
      },
      formattedRecurrenceParams() {
        const dayOffset = 24 * 60 * 60 * 1000 * (this.start_time.getDay() - createDateAsUTC(this.start_time).getDay())
        const recurrenceParams = this.recurrenceParams
        let til = new Date(recurrenceParams.til)
        til.setHours(this.start_time.getHours(), this.start_time.getMinutes() + this.duration)
        try {
          const formattedRecurrenceParams = {
            is_recurring: recurrenceParams.is_recurring,
            recurring: recurrenceParams.is_recurring,
            time_start: createDateAsUTC(new Date(this.start_time.getTime() + dayOffset)).toISOString(),
            frequency: recurrenceParams.frequency,
            rule_type: recurrenceParams.rule_type,
            monthly_type: recurrenceParams.monthly_type,
            days_of_week: JSON.stringify(recurrenceParams.days_of_week),
          }
          if (this.recurrenceParams.endsType !== 'never') {
            formattedRecurrenceParams.count = recurrenceParams.endsType === 'after' ? recurrenceParams.count : null
            formattedRecurrenceParams.til = recurrenceParams.endsType === 'on' ? createDateAsUTC(til).toISOString() : null
          }
          return formattedRecurrenceParams;
        } catch (e) {
          console.error(e)
        }
      },
      recurringPassedLimit() {
        if(this.$hasFeature('virtual_meetings') || this.recurrenceParams.endsType === 'never') {
          return false
        }
        return this.recurrenceParams.count > this.recurring_limit_number
          || this.recurrenceCount > this.recurring_limit_number;
      },
      isValid() {
        return !this.recurringPassedLimit && parseInt(this.formattedRecurrenceParams.frequency) && (parseInt(this.formattedRecurrenceParams.count) || this.formattedRecurrenceParams.til || this.recurrenceParams.endsType === 'never')
      }
    },
  }

</script>

<style lang="scss" scoped>
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_i18n-mixins.scss";
  @import "@/assets/scss/abstract/_mixins.scss";
  .recurrence-string {
    flex-grow: 0;
    padding: 12px 0;
    cursor: pointer;
    font-style: normal !important;
    font-weight: 500 !important;
    font-size: 15px !important;
    color: #478FFF !important;
    @include md-and-up {
      padding: 0;
      font-size: 13px !important;
    }
    i {
      color: #478FFF;
      margin-inline-end: 4px;
    }
    .text {
      text-decoration-line: underline;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
  .recurrence-selector-menu-content {
    max-width: 327px;
    .mobile-header {
      padding: 16px 12px 16px 24px;
      max-height: 56px;
    }
    .v-alert {
      margin: 0;
    }
    .recurrence-inputs {
      padding: 16px 24px;

      @include md-and-up {
        border: none;
        padding: 16px;
        padding-bottom: 0;
      }
    }
    .recurrence-footer {
      padding: 12px 24px;
      @include md-and-up {
        padding: 16px;
        padding-top: 0;
      }
    }
    .mobile-title {
      font-family: var(--primary-font-family);
      font-size: 17px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px;
      letter-spacing: 0px;

    }
    &.v-card:not(.v-sheet--tile):not(.v-card--shaped) {
      border-radius: 6px;
    }
    .recurrence-switch {
      margin-bottom: 18px;
    }
    .frequency-input {
      max-width: 118px;
      min-width: 118px;
      margin-inline-end: 8px;
    }
    .recurrence-type-select {
      margin-bottom: 16px;
    }
    .monthly-type-radio {
      margin: 0;
      padding: 0;
      margin-bottom: 16px;

      ::v-deep {
        label {
          font-size: 15px;
          color: inherit;
        }

        .v-input--selection-controls__input {
          width: 16px;
          height: 16px;
        }

        .v-input--selection-controls__ripple {
          left: -16px;
        }
      }
    }

    .end-type-select {
      max-width: 118px;
      margin-bottom: 16px;
      margin-inline-end: 8px;
    }
    .end-type-select:only-child {
      max-width: unset;
      margin-inline-end: 0;
    }
    .weekly-days-select {
      margin-bottom: 16px;
    }
    .day-buttons-title {
      font-family: var(--primary-font-family);
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 14px;

      margin-bottom: 8px;
    }
    .day-btn {
      background-color: #EAEDF2;
      margin-inline-end: auto;
      padding: 0 !important;
      border-radius: 1000px !important;
      border: none;
      &.v-btn--active {
        background-color: currentColor;
        ::v-deep {
          span {
            color: #FFFFFF;
          }
        }
      }
      &:first-child {
        margin-inline-start: 0;
      }
      &:last-child {
        margin-inline-end: 0;
      }
      ::v-deep {
        span {
          font-family: var(--primary-font-family);
          font-size: 12px;
          font-style: normal;
          font-weight: 500;
          line-height: 24px;
          text-align: center;
          letter-spacing: 0;
        }
      }
    }
    .btn {
      font-size: 13px;
      font-style: normal;
      font-weight: 600;
      line-height: 16px;
      letter-spacing: 0px;
      height: 40px;
      text-transform: none;
    }
    .cancel-btn {
      margin: 0 4px;
    }
    .done-btn {
      color: #fff;
      &[disabled] {
        &.v-btn--disabled.v-btn--contained {
          background-color: rgba(0, 0, 0, 0.07) !important;
        }
      }
    }
  }
</style>

<style lang="scss">
  .recurrence-selector-content {
    font-family: var(--primary-font-family);
    height: revert;
  }

</style>
